import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import "./datatable.scss";
import { DataGrid } from "@mui/x-data-grid";
import axiosInstance from "../../api/axios";
import { toast } from "react-toastify";
import { getRecentThesisSubmitted } from "../../services/thesisAssignedService";

/**
 * AssignExaminerList.jsx
 * - DataGrid (read-only)
 * - Modal editor for Viva schedule (reliable input handling)
 */

const Modal = ({ open, onClose, children }) => {
  if (!open) return null;
  return (
    <div
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.35)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 1300,
      }}
      onMouseDown={(e) => {
        // close when clicking backdrop
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div
        role="dialog"
        aria-modal="true"
        style={{
          width: 480,
          background: "#fff",
          borderRadius: 8,
          padding: 20,
          boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
        }}
      >
        {children}
      </div>
    </div>
  );
};

export const AssignExaminerList = () => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // modal state
  const [modalOpen, setModalOpen] = useState(false);
  const [editingRow, setEditingRow] = useState(null);

  // form state for modal
  const [form, setForm] = useState({
    vivaId: null, // existing viva_schedule.id (if any)
    thesisStatusId: null,
    date: "",
    startTime: "",
    endTime: "",
  });

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await getRecentThesisSubmitted();
      const data = res?.data || [];
      setRows(data);

    } catch (err) {
      console.error("fetch error", err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  // Open modal and populate form from row
  const openModalForRow = (row) => {
    if (!row) return;
    const vivaId = row.vivaId ?? null;
    const date = row.date ?? row.vs_date ?? "";
    const startTime = row.startTime ?? row.start_time ?? row.vs_start_time ?? "";
    const endTime = row.endTime ?? row.end_time ?? row.vs_end_time ?? "";

    setForm({
      vivaId,
      thesisStatusId: row.id, // thesis_status.id
      date: date || "",
      startTime: startTime || "",
      endTime: endTime || "",
    });
    setEditingRow(row);
    setModalOpen(true);
  };

  const closeModal = () => {
    setModalOpen(false);
    setEditingRow(null);
    setTimeout(
      () =>
        setForm({
          vivaId: null,
          thesisStatusId: null,
          date: "",
          startTime: "",
          endTime: "",
        }),
      200
    );
  };

  const validateForm = () => {
  if (!form.date) {
    toast.error("Please select a viva date.");
    return false;
  }
  if (!form.startTime) {
    toast.error("Please select a start time.");
    return false;
  }
  if (!form.endTime) {
    toast.error("Please select an end time.");
    return false;
  }
  if (form.startTime >= form.endTime) {
    toast.error("Start time must be before end time.");
    return false;
  }
  return true;
};


  const handleSave = async () => {
    if (!validateForm()) return;

    const payload = {
      id: form.vivaId ?? null,
      thesisStatusId: form.thesisStatusId,
      date: form.date || null,
      startTime: form.startTime || null,
      endTime: form.endTime || null,
    };

    console.log("Viva payload ->", payload);

    try {
      if (form.vivaId) {
        await axiosInstance.put("/updateVivaSchedule", payload);
        toast.success("Viva schedule updated");
      } else {
        await axiosInstance.post("/addVivaSchedule", payload);
        toast.success("Viva schedule created");
      }
      await fetchData();
      closeModal();
    } catch (err) {
      console.error("Save failed", err);
      toast.error("Failed to save viva schedule");
    }
  };

  // Safe valueGetters to avoid params.row undefined crash
  const safeGet = (params, ...keys) => {
    const row = params?.row;
    if (!row) return "";
    for (let k of keys) {
      if (row[k] != null) return row[k];
    }
    return "";
  };

  const shareViva = async (vivaId) => {
    try {
      axiosInstance.put(`/shareVivaSchedule/${vivaId}`);
      toast.success("Viva shared with student");
      fetchData();
    } catch (err) {
      toast.error("Failed to share viva");
      console.error(err);
    }
  };


  const columns = [
    { field: "thesis_title", headerName: "Title", width: 240 },
    { field: "student_name", headerName: "Student Name", width: 150 },
    { field: "enrollment_number", headerName: "Enrollment No", width: 150 },
    { field: "report_type", headerName: "Report Type", width: 140 },
    { field: "term_type", headerName: "Submission Type", width: 140 },
    { field: "date_submitted", headerName: "Date Submitted", width: 140 },
    { field: "date", headerName: "Viva Date", width: 140 },
    { field: "startTime", headerName: "Viva Start Time", width: 140 },
    { field: "endTime", headerName: "Viva End Time", width: 140 },

    {
      field: "action",
      headerName: "Action",
      width: 400,
      renderCell: (params) => {
        // guard
        if (!params || !params.row) return null;
        const { id, reviewer_id1, reviewer_id2, examiner_id1, examiner_id2 } = params.row;
        return (
          <div className="cellAction" style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <div className="viewButton">
              <Link to={`view-assigned/${id}`}>View/Edit</Link>
            </div>

            {(!examiner_id1 || !examiner_id2 || !reviewer_id1 || !reviewer_id2) ? (
              <div className="editButton">
                <Link to={`assignexaminer/${id}`}>Assign Examiners</Link>
              </div>
            ) : (
              <div className="editButton">Examiners Assigned</div>
            )}

            <button
              onClick={(e) => {
                e.stopPropagation();
                if (!params || !params.row) return;
                openModalForRow(params.row);
              }}
              style={{
                marginLeft: 8,
                padding: "6px 10px",
                background: "#1976d2",
                color: "#fff",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
              }}
            >
              Edit Viva
            </button>
            
            {(params.row.date && params.row.startTime && params.row.endTime) ? (
              
              params.row.shareToStudent ? (
                <button
                  disabled
                  style={{
                    marginLeft: 8,
                    padding: "6px 10px",
                    background: "#9E9E9E",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    cursor: "not-allowed",
                  }}
                >
                  Shared
                </button>
              ) : (
                // If not shared -> show active Share button
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    shareViva(params.row.vivaId);
                  }}
                  style={{
                    marginLeft: 8,
                    padding: "6px 10px",
                    background: "green",
                    color: "#fff",
                    border: "none",
                    borderRadius: 4,
                    cursor: "pointer",
                  }}
                >
                  Share
                </button>
              )

            ) : null}


          </div>
        );
      },
    },
  ];

  return (
    <div>
      <div className="datatable">
        <div className="datatableTitle">Recent Submissions List</div>

        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          pageSize={10}
          autoHeight
          
        />
      </div>

      <Modal open={modalOpen} onClose={closeModal}>
        <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
          <div>
            <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>Student</label>
            <div style={{ fontWeight: 600 }}>{editingRow?.student_name ?? "-"}</div>
            <div style={{ color: "#666", fontSize: 12 }}>{editingRow?.enrollment_number ?? ""}</div>
          </div>

          <div>
            <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>Viva Date</label>
            <input
  type="date"
  value={form.date || ""}
  onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
  style={{
    width: "100%",
    padding: "8px",
    borderRadius: 4,
    border: form.date ? "1px solid #ccc" : "1px solid red",
  }}
/>

          </div>

          <div style={{ display: "flex", gap: 8 }}>
            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>Start Time</label>
              <input
  type="time"
  value={form.startTime || ""}
  onChange={(e) => setForm((p) => ({ ...p, startTime: e.target.value }))}
  style={{
    width: "100%",
    padding: "8px",
    borderRadius: 4,
    border: form.startTime ? "1px solid #ccc" : "1px solid red",
  }}
/>

            </div>

            <div style={{ flex: 1 }}>
              <label style={{ display: "block", fontSize: 13, marginBottom: 6 }}>End Time</label>
              <input
  type="time"
  value={form.endTime || ""}
  onChange={(e) => setForm((p) => ({ ...p, endTime: e.target.value }))}
  style={{
    width: "100%",
    padding: "8px",
    borderRadius: 4,
    border: form.endTime ? "1px solid #ccc" : "1px solid red",
  }}
/>



            </div>
          </div>

          <div style={{ 
  marginTop: 10, 
  fontSize: 12, 
  color: "#555", 
  background: "#f8f8f8", 
  padding: "10px 12px", 
  borderRadius: 6,
  lineHeight: "18px"
}}>
  <strong>Tip:</strong>  
  Time fields may show a default value (such as <em>12:30 PM</em>).  
  Please enter the full time manually — including <strong>hours</strong>, 
  <strong>minutes</strong>, and <strong>AM/PM</strong>.  
  If only part of the field is edited, the system may treat the time as not entered.
</div>


          <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
            <button
              onClick={handleSave}
              disabled={!form.date || !form.startTime || !form.endTime}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                background: "#1976d2",
                color: "#fff",
                border: "none",
                cursor: (!form.date || !form.startTime || !form.endTime) ? "not-allowed" : "pointer",
              }}
            >
              Save
            </button>

            <button
              onClick={closeModal}
              style={{
                padding: "8px 14px",
                borderRadius: 6,
                background: "#eee",
                color: "#222",
                border: "none",
                cursor: "pointer",
              }}
            >
              Cancel
            </button>
          </div>

          
        </div>
      </Modal>
    </div>
  );
};

export default AssignExaminerList;
